import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import ManageIncomingMail from "./ManageIncomingMail/ManageIncomingMail";
import ManageMailInboxAddressees from "./ManageMailInboxAddressees/ManageMailInboxAddressees";
import ManageOutgoingMail from "./ManageOutgoingMail/ManageOutgoingMail";
import { useAppSelector } from "../../custom-hooks/redux/redux";
import useQuery from "../../custom-hooks/useQuery/useQuery";
import { Panel } from "../../reusable-components/MultipleTabs/interfaces";
import MultipleTabs from "../../reusable-components/MultipleTabs/MultipleTabs";
import { AUTHORIZATIONS } from "../../utils/constants/authorization-constants";

export enum ManageMailRoutes {
  INCOMING = "incoming",
  OUTGOING = "outgoing",
  RECIPIENTS = "recipients",
}

const ManageMail = () => {
  const query = useQuery();
  const { t } = useTranslation();
  const tRP = "pages.manage_mail_page";

  const { currentUserAuthorizations } = useAppSelector((state) => state.currentUserReducer);
  const mailInboxAuth = currentUserAuthorizations?.allowedMailInboxes?.flatMap(
    (allowedMailInbox) => allowedMailInbox.value
  );

  const hasAccessToIncoming =
    (mailInboxAuth?.includes(AUTHORIZATIONS.MailInboxAccess.CanUploadIncoming) ||
      mailInboxAuth?.includes(AUTHORIZATIONS.MailInboxAccess.CanClassifyIncoming) ||
      mailInboxAuth?.includes(AUTHORIZATIONS.MailInboxAccess.CanReadIncoming)) ??
    false;

  const hasAccessToOutgoing =
    (mailInboxAuth?.includes(AUTHORIZATIONS.MailInboxAccess.CanUploadOutgoing) ||
      mailInboxAuth?.includes(AUTHORIZATIONS.MailInboxAccess.CanClassifyOutgoing) ||
      mailInboxAuth?.includes(AUTHORIZATIONS.MailInboxAccess.CanReadOutgoing) ||
      mailInboxAuth?.includes(AUTHORIZATIONS.MailInboxAccess.CanProcessOutgoing)) ??
    false;

  const canManageLetterAddressees =
    (mailInboxAuth?.includes(AUTHORIZATIONS.MailInboxAccess.CanUploadIncoming) ||
      mailInboxAuth?.includes(AUTHORIZATIONS.MailInboxAccess.CanUploadOutgoing)) ??
    false;

  const isRecipient = mailInboxAuth?.includes(AUTHORIZATIONS.MailInboxAccess.isRecipient) ?? false;

  const checkPanelAccess = useCallback((showPanel: boolean, panel: Panel) => (showPanel ? [panel] : []), []);

  const panels: Panel[] = useMemo(
    () => [
      ...checkPanelAccess(hasAccessToIncoming || isRecipient, {
        tabLabelRootPath: `${tRP}.tabs.incoming_mail`,
        tabPanelComponent: <ManageIncomingMail />,
        routePath: ManageMailRoutes.INCOMING,
      }),
      ...checkPanelAccess(hasAccessToOutgoing, {
        tabLabelRootPath: `${tRP}.tabs.outgoing_mail`,
        tabPanelComponent: <ManageOutgoingMail />,
        routePath: ManageMailRoutes.OUTGOING,
      }),
      ...checkPanelAccess(canManageLetterAddressees, {
        tabLabelRootPath: `${tRP}.tabs.letter_addressees`,
        tabPanelComponent: <ManageMailInboxAddressees />,
        routePath: ManageMailRoutes.RECIPIENTS,
      }),
    ],
    [canManageLetterAddressees, checkPanelAccess, hasAccessToIncoming, hasAccessToOutgoing, isRecipient]
  );

  const selectedTab = useMemo(() => {
    const foundIndex = panels.findIndex((panel) => panel.routePath === query.get("tab"));
    return foundIndex >= 0 ? foundIndex : 0;
  }, [query, panels]);

  return (
    <>
      <MultipleTabs
        panels={panels}
        selectedTab={selectedTab}
      />
    </>
  );
};

export default ManageMail;
